"use client"

import { useState } from "react"
import { Filter, User, LogOut, Menu } from "lucide-react"
import { Button } from "./button"
import { Input } from "./input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu"
import { PulseLogo } from "./pulse-logo"
import { NotificationDropdown } from "./notification-dropdown"
import type { DeviceStats } from '../../types';

interface DashboardHeaderProps {
  onLogout: () => void;
  onFiltersChange?: (filters: {
    client: string;
    site: string;
    deviceType?: string;
  }) => void;
  onExport?: () => void;
  stats?: DeviceStats[];
  onSidebarToggle?: () => void;
  title?: string;
  showFilters?: boolean;
}

export function DashboardHeader({
  onLogout,
  onFiltersChange,
  onExport,
  stats = [],
  onSidebarToggle,
  title = "Dashboard",
  showFilters = true,
}: DashboardHeaderProps) {
  const [clientSearch, setClientSearch] = useState("")
  const [siteSearch, setSiteSearch] = useState("")
  const [filters, setFilters] = useState({
    client: "all",
    site: "all",
    deviceType: "onpremise",
  })

  const updateFilters = (key: string, value: string) => {
    let newFilters = { ...filters, [key]: value }

    // When client changes, reset site if the current site doesn't belong to the new client
    if (key === 'client' && filters.site !== 'all') {
      const clientStats = value === 'all' ? stats : stats.filter(s => s.client_id === value);
      const availableSiteIds = new Set(clientStats.map(s => s.site_id));
      if (!availableSiteIds.has(filters.site)) {
        newFilters = { ...newFilters, site: 'all' };
      }
    }

    setFilters(newFilters)
    onFiltersChange?.(newFilters)
  }

  // Extract unique clients from full stats
  const allClients = Array.from(new Set(stats.map(stat => stat.client_id))).sort()

  // Scope available sites to the selected client so the dropdown only shows valid combinations
  const clientScopeStats = filters.client === 'all'
    ? stats
    : stats.filter(s => s.client_id === filters.client)
  const allSites = Array.from(new Set(clientScopeStats.map(stat => stat.site_id))).sort()

  const filteredClients = allClients.filter((client: string) =>
    client.toLowerCase().includes(clientSearch.toLowerCase())
  )
  const filteredSites = allSites.filter((site: string) =>
    site.toLowerCase().includes(siteSearch.toLowerCase())
  )

  return (
    <div style={{ backgroundColor: "#0d1117", boxShadow: "0 2px 10px rgba(0,0,0,0.35)" }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6" style={{ height: "64px" }}>
        {/* Left: menu + brand breadcrumb */}
        <div className="flex items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onSidebarToggle}
            className="border-0 h-9 w-9 p-0 rounded-lg transition-colors hover:bg-white/5"
            style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex items-center" style={{ marginLeft: "16px" }}>
            <PulseLogo size="sm" showText={false} />
            <span
              className="text-base font-semibold"
              style={{
                marginLeft: "8px",
                background: "linear-gradient(135deg, #ff8697 0%, #ff4d73 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              PULSE
            </span>
            <span
              className="text-sm select-none"
              style={{ marginLeft: "8px", marginRight: "8px", color: "rgba(255,255,255,0.35)" }}
            >
              /
            </span>
            <span className="text-sm font-medium" style={{ color: "#c9d1d9" }}>
              {title}
            </span>
          </div>
        </div>

        {/* Right: notification + user */}
        <div className="flex items-center" style={{ gap: "12px" }}>
          <NotificationDropdown
            style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-0 h-9 w-9 p-0 rounded-lg transition-colors hover:bg-white/5"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ backgroundColor: "#1a1a1a", borderColor: "#2a2a2a" }}>
              <DropdownMenuItem onClick={onLogout} style={{ color: "#94a3b8" }}>
                <LogOut className="h-4 w-4 mr-2" />
                <span className="text-base">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters Bar */}
      {showFilters && (
        <div className="px-6 pb-5">
          <div
            className="rounded-2xl px-5 py-4"
            style={{
              backgroundColor: "#0f1717",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <div className="flex items-end gap-4 flex-wrap">

              {/* Client Filter */}
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>
                  Client
                </label>
                <Select value={filters.client} onValueChange={(value) => updateFilters('client', value)}>
                  <SelectTrigger
                    className="w-48 h-9 text-sm rounded-xl focus:ring-1 focus:ring-[#00A9A7] transition-all"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent
                    style={{
                      backgroundColor: "#0f1717",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div className="p-2">
                      <Input
                        placeholder="Search clients..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className="h-8 text-sm rounded-lg"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          color: "rgba(255,255,255,0.85)",
                        }}
                      />
                    </div>
                    <SelectItem value="all" style={{ color: "rgba(255,255,255,0.85)" }}>All Clients</SelectItem>
                    {filteredClients.map(client => (
                      <SelectItem key={client} value={client} style={{ color: "rgba(255,255,255,0.85)" }}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Site Filter */}
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>
                  Site
                </label>
                <Select value={filters.site} onValueChange={(value) => updateFilters('site', value)}>
                  <SelectTrigger
                    className="w-44 h-9 text-sm rounded-xl focus:ring-1 focus:ring-[#00A9A7] transition-all"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <SelectValue placeholder="All Sites" />
                  </SelectTrigger>
                  <SelectContent
                    style={{
                      backgroundColor: "#0f1717",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div className="p-2">
                      <Input
                        placeholder="Search sites..."
                        value={siteSearch}
                        onChange={(e) => setSiteSearch(e.target.value)}
                        className="h-8 text-sm rounded-lg"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          color: "rgba(255,255,255,0.85)",
                        }}
                      />
                    </div>
                    <SelectItem value="all" style={{ color: "rgba(255,255,255,0.85)" }}>All Sites</SelectItem>
                    {filteredSites.map(site => (
                      <SelectItem key={site} value={site} style={{ color: "rgba(255,255,255,0.85)" }}>
                        {site}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Device Type */}
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>
                  Device type
                </label>
                <Select value={filters.deviceType || "all"} onValueChange={(value) => updateFilters('deviceType', value)}>
                  <SelectTrigger
                    className="w-40 h-9 text-sm rounded-xl focus:ring-1 focus:ring-[#00A9A7] transition-all"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent
                    style={{
                      backgroundColor: "#0f1717",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <SelectItem value="all" style={{ color: "rgba(255,255,255,0.85)" }}>All (incl. gateways)</SelectItem>
                    <SelectItem value="onpremise" style={{ color: "rgba(255,255,255,0.85)" }}>On-premise only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <label className="text-xs font-semibold uppercase tracking-wider invisible">Clear</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const clearedFilters = {
                      client: "all",
                      site: "all",
                      deviceType: "onpremise",
                    };
                    setFilters(clearedFilters);
                    setClientSearch("");
                    setSiteSearch("");
                    onFiltersChange?.(clearedFilters);
                  }}
                  className="h-9 px-4 text-sm font-medium rounded-xl border-0 transition-all"
                  style={{
                    backgroundColor: "rgba(0, 169, 167, 0.1)",
                    color: "#00A9A7",
                    border: "1px solid rgba(0, 169, 167, 0.25)",
                  }}
                >
                  <Filter className="h-3.5 w-3.5 mr-1.5" />
                  Clear Filters
                </Button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  )
}
